import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';
import { getAllPosts, formatPostDate } from '../data/insights';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const Insights = () => {
  const { language } = useLanguage();
  const tr = (key) => t(language, key);
  const posts = getAllPosts();

  useDocumentMeta({
    title: `${tr('insights.metaTitle')} | BIMCORP MX`,
    description: tr('insights.metaDescription'),
  });

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--midnight)', color: 'var(--pearl)' }}
      data-testid="insights-page"
    >
      {/* Outer thin-line frame */}
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-14 py-16 md:py-24">
        <div
          className="border"
          style={{ borderColor: 'rgba(241,236,228,0.18)' }}
        >
          {/* Header */}
          <header
            className="px-8 md:px-14 py-12 md:py-16 border-b"
            style={{ borderColor: 'rgba(241,236,228,0.18)' }}
          >
            <p
              className="text-xs uppercase tracking-[0.3em] mb-6"
              style={{ color: 'var(--light-blue)' }}
            >
              {tr('insights.eyebrow')}
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6"
              style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', color: 'var(--pearl)', textAlign: 'center' }}
            >
              {tr('insights.title')}
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{ color: 'rgba(254,253,244,0.72)', textAlign: 'justify' }}
            >
              {tr('insights.subtitle')}
            </p>
          </header>

          {/* Articles archive */}
          <div className="grid grid-cols-1 md:grid-cols-2">
            {posts.length === 0 && (
              <div className="col-span-full px-8 md:px-14 py-16 text-center" style={{ color: 'rgba(241,236,228,0.6)' }}>
                {tr('insights.empty')}
              </div>
            )}
            {posts.map((post, idx) => {
              const isLastInRow = (idx + 1) % 2 === 0;
              const showRightBorder = !isLastInRow;
              return (
                <Link
                  key={post.slug}
                  to={`/insights/${post.slug}`}
                  className="group block px-8 md:px-12 py-10 md:py-14 transition-colors"
                  style={{
                    borderTop: idx >= 2 ? '1px solid rgba(241,236,228,0.18)' : 'none',
                    borderRight: showRightBorder ? '1px solid rgba(241,236,228,0.18)' : 'none',
                  }}
                  data-testid={`insights-card-${post.slug}`}
                >
                  <div className="flex items-baseline justify-between mb-6">
                    <span
                      className="text-xs uppercase tracking-[0.25em]"
                      style={{ color: 'var(--light-blue)' }}
                    >
                      {post.category}
                    </span>
                    <span
                      className="text-xs"
                      style={{ color: 'rgba(241,236,228,0.55)' }}
                    >
                      {formatPostDate(post.publishedAt, language)}
                    </span>
                  </div>

                  <h2
                    className="text-2xl md:text-3xl leading-snug mb-5 group-hover:underline underline-offset-4 decoration-1"
                    style={{
                      fontFamily: 'Helvetica Neue, Arial, sans-serif',
                      color: 'var(--pearl)',
                      textAlign: 'left',
                    }}
                  >
                    {post.title}
                  </h2>

                  <p
                    className="text-sm md:text-base leading-relaxed mb-6"
                    style={{ color: 'rgba(254,253,244,0.72)', textAlign: 'justify' }}
                  >
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <span
                      className="text-xs uppercase tracking-[0.2em]"
                      style={{ color: 'rgba(241,236,228,0.55)' }}
                    >
                      {tr('insights.by')} {post.author}
                    </span>
                    <span
                      className="text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
                      style={{ color: 'var(--light-blue)' }}
                    >
                      {tr('insights.readMore')}
                      <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;
